import { CalendarIcon, ClockIcon, DurationIcon, LockIcon, UserIcon, WifiIcon } from "@xola/icons";
import { Badge, Dot } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, isValidTimeZoneId, PurchaseItemStatus } from "common";
import { Dayjs } from "dayjs";
import pluralize from "pluralize";
import React from "react";
import { Event, Experience, ExperiencePurchaseItem } from "types";
import { usePurchase } from "../../hooks/resources/usePurchase";
import { useResource } from "../../hooks/useResource";
import { getStart, isAllDay } from "../../models/Event";
import { getCustomerName } from "../../models/Purchase";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { Loader } from "../Loader";
import { formatDuration } from "@/helpers/durationHelper";
import { useCollection } from "@/hooks/useCollection";

interface EventSummaryTileProps {
    event: Event;
    className?: string;
    quantity?: number;
    timezone?: string;
}

export const EventSummaryTile = ({ event, quantity, className, timezone }: EventSummaryTileProps) => {
    const { data: purchaseItems, isLoading: isLoadingPurchaseItems } = useCollection<ExperiencePurchaseItem>(
        "purchaseItems",
        {
            event: event.id,
            "status[in]": [
                PurchaseItemStatus.CONFIRMED,
                PurchaseItemStatus.PENDING_ACCEPT,
                PurchaseItemStatus.PENDING_MINIMUM,
            ].join(","),
            limit: 1,
        },
        { enabled: event.private },
    );

    const purchaseItem = purchaseItems?.[0];

    const { purchase, isLoadingPurchase } = usePurchase(purchaseItem?.purchase.id, null, {
        enabled: event.private && !!purchaseItem,
    });

    const { data: experience, isLoading: isLoadingProduct } = useResource<Experience>([
        "products",
        event.experience.id,
    ]);

    if (isLoadingPurchaseItems || isLoadingPurchase || isLoadingProduct) {
        return <Loader />;
    }

    const allDay = isAllDay(event, experience!);

    const startDateTime =
        !!event?.virtualMeeting && isValidTimeZoneId(timezone) ? getStart(event).tz(timezone) : getStart(event);

    return (
        <div className={clsx("mb-5 flex space-x-1 rounded-lg bg-gray-lighter p-4", className)}>
            <ProductImage size="small" className="mr-0 rounded-lg" product={experience!} />
            <div className="w-full space-y-2">
                <NoTranslate className="text-base font-bold leading-4" data-testid="event-summary-experience-name">
                    {experience?.name}
                </NoTranslate>

                <DateInfoLine
                    date={startDateTime}
                    label=""
                    quantity={quantity}
                    allDay={allDay}
                    experience={experience}
                />

                <div className="flex space-x-2">
                    {event.virtualMeeting ? (
                        <Badge color="primary">
                            <WifiIcon />
                        </Badge>
                    ) : null}
                    {event.private ? (
                        <Badge color="warning" icon={<LockIcon />} className="no-translate">
                            {purchase ? getCustomerName(purchase) : "N/A"}
                        </Badge>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

interface DateInfoLineProps {
    date: Dayjs;
    label: string;
    allDay: boolean;
    quantity: number | undefined;
    experience?: Experience;
}

export const DateInfoLine = ({ date, label, allDay, quantity, experience }: DateInfoLineProps) => {
    return (
        <div className="flex space-x-2 text-gray-darker" data-testid="event-summary-arrival">
            <span>
                <span>
                    <CalendarIcon />{" "}
                    <NoTranslate>
                        {label} {formatDate(date, DateFormat.DAY_DATE)}
                    </NoTranslate>
                </span>

                {allDay ? null : (
                    <>
                        <span className="relative bottom-1">
                            <Dot color="secondary" />
                        </span>
                        <NoTranslate>
                            <ClockIcon /> {formatDate(date, DateFormat.TIME)}
                        </NoTranslate>
                    </>
                )}
            </span>

            {experience?.eventDuration && (
                <>
                    <span className="relative bottom-1">
                        <Dot color="secondary" />
                    </span>
                    <span className="text-gray-darker" data-testid="event-summary-tile-duration">
                        <DurationIcon /> <NoTranslate>{formatDuration(experience.eventDuration)}</NoTranslate>
                    </span>
                </>
            )}

            {(quantity || quantity === 0) && (
                <>
                    <span className="relative bottom-1">
                        <Dot color="secondary" />
                    </span>
                    <div className="text-gray-darker">
                        <UserIcon /> <NoTranslate>{quantity}</NoTranslate> {pluralize("guest", quantity)}
                    </div>
                </>
            )}
        </div>
    );
};
